// "Face data isn't the photo", animated (one 10s loop, CSS keyframes on inline SVG — see webUploader/AnimatedFigure):
//   1. a photo (bib 1234, sold) with its face outlined, linked to its face template (a mesh of dots);
//   2. the template's lines fade and its dots drift apart and vanish; the outline and the link go too;
//   3. "Deleted" is left where the template was; the photo, its bib and its sale are untouched.
// Reduced motion: frozen at step 3.
import type { CSSProperties } from "react";
import { AnimatedFigure } from "@/components/webUploader/AnimatedFigure";
import { EASE, kf, type Stop } from "@/components/webUploader/keyframes";
import type { FaceDataCopy } from "@/content/faceData2026";
import { Runner, TemplateDots, scatter } from "./scene";

const SCOPE = "fddv";
const DUR = 10;
const FREEZE = 80;

const PHOTO = { x: 18, y: 34, w: 168, h: 144 };
const TPL = { x: 238, y: 34, w: 144, h: 144 };
const COLS = 5;
const ROWS = 5;
const GAP = 22;

const IN = 4;
const DEL = [36, 58] as const;
const OUT = 94;

const name = (el: string) => `${SCOPE}-${el}`;
const anim = (el: string): CSSProperties => ({ animationName: name(el) });

function buildKeyframes(): string {
  const out: string[] = [];
  const keep = (el: string, until: number, gone = 6) =>
    kf(name(el), [[0, "opacity:0"], [IN - 2, "opacity:0"], [IN, "opacity:1"], [until, "opacity:1"], [until + gone, "opacity:0"], [OUT + 3, "opacity:0"], [100, "opacity:0"]]);
  out.push(keep("lines", DEL[0], 8));
  out.push(keep("face", DEL[0] + 6, 6));
  out.push(keep("link", DEL[0] + 4, 6));
  out.push(keep("frame", DEL[0] + 10, 8));
  scatter(COLS * ROWS, 46).forEach(([dx, dy], i) => {
    const start = DEL[0] + 2 + (i % 7) * 1.6;
    out.push(kf(name(`d${i}`), [
      [0, "transform:translate(0px,0px);opacity:0"],
      [IN - 2, "transform:translate(0px,0px);opacity:0"],
      [IN, "transform:translate(0px,0px);opacity:1"],
      [start, `transform:translate(0px,0px);opacity:1;${EASE}`],
      [Math.min(start + 12, DEL[1] + 8), `transform:translate(${dx}px,${dy}px);opacity:0`],
      [100, `transform:translate(${dx}px,${dy}px);opacity:0`],
    ] as Stop[]));
  });
  out.push(kf(name("gone"), [[0, "opacity:0"], [DEL[1], "opacity:0"], [DEL[1] + 3, "opacity:1"], [OUT, "opacity:1"], [OUT + 3, "opacity:0"], [100, "opacity:0"]]));
  // the photo's own tags pulse once after the deletion: still there
  out.push(kf(name("keep"), [[0, "transform:scale(1)"], [DEL[1] + 6, "transform:scale(1)"], [DEL[1] + 9, "transform:scale(1.12)"], [DEL[1] + 12, "transform:scale(1)"], [100, "transform:scale(1)"]]));
  return out.join("");
}

const KEYFRAMES = buildKeyframes();

const tagW = (text: string) => 18 + text.length * 6.2;

const Tag = ({ x, y, text, tone }: { x: number; y: number; text: string; tone: "ink" | "green" }) => {
  const w = tagW(text);
  return (
    <g data-a="" data-flip="" style={anim("keep")}>
      <rect x={x} y={y} width={w} height={22} rx={11} className={tone === "green" ? "fill-emerald-600" : "fill-foreground"} />
      <text x={x + w / 2} y={y + 15} fontSize={10.5} fontWeight={700} textAnchor="middle" className={tone === "green" ? "" : "fill-background"} fill={tone === "green" ? "white" : undefined}>{text}</text>
    </g>
  );
};

export const DissolveFigure = ({ copy, pauseLabel, playLabel }: { copy: FaceDataCopy["dissolve"]; pauseLabel: string; playLabel: string }) => {
  const s = copy.scene;
  const tplCx = TPL.x + TPL.w / 2;
  const dotsX = tplCx - ((COLS - 1) * GAP) / 2;
  const dotsY = TPL.y + 26 + (TPL.h - 26 - (ROWS - 1) * GAP) / 2;
  return (
    <AnimatedFigure
      scope={SCOPE}
      durationS={DUR}
      freezeAt={FREEZE}
      keyframes={KEYFRAMES}
      label={copy.label}
      description={copy.caption}
      pauseLabel={pauseLabel}
      playLabel={playLabel}
      className="rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-6"
    >
      <svg viewBox="0 0 400 236" className="block h-auto w-full" role="presentation" data-testid="dissolve-figure">
        <text x={PHOTO.x + PHOTO.w / 2} y={20} fontSize={12} fontWeight={600} textAnchor="middle" className="fill-muted-foreground">{s.photo}</text>
        <text x={tplCx} y={20} fontSize={12} fontWeight={600} textAnchor="middle" className="fill-muted-foreground">{s.template}</text>

        {/* the link from the face to its template */}
        <g data-a="" style={anim("link")}>
          <path d={`M ${PHOTO.x + PHOTO.w / 2 + 24} ${PHOTO.y + 30} C 214 ${PHOTO.y + 30}, 214 ${TPL.y + 70}, ${TPL.x} ${TPL.y + 70}`} fill="none" className="stroke-primary" strokeWidth={1.5} strokeDasharray="4 3" />
        </g>

        {/* the photo: it stays */}
        <g transform={`translate(${PHOTO.x},${PHOTO.y})`}>
          <Runner w={PHOTO.w} h={PHOTO.h} clipId={`${SCOPE}-clip`} faceStyle={anim("face")} />
        </g>
        <Tag x={PHOTO.x} y={PHOTO.y + PHOTO.h + 8} text={s.bib} tone="ink" />
        <Tag x={PHOTO.x + tagW(s.bib) + 8} y={PHOTO.y + PHOTO.h + 8} text={`✓ ${s.sale}`} tone="green" />

        {/* the face template: it dissolves */}
        <g data-a="" style={anim("frame")}>
          <rect x={TPL.x} y={TPL.y} width={TPL.w} height={TPL.h} rx={14} className="fill-accent stroke-primary" strokeWidth={1.5} />
        </g>
        <rect x={TPL.x} y={TPL.y} width={TPL.w} height={TPL.h} rx={14} fill="none" className="stroke-border" strokeWidth={1.5} strokeDasharray="5 4" />
        <TemplateDots cols={COLS} rows={ROWS} x={dotsX} y={dotsY - 8} gap={GAP} r={3.2} dotStyle={(i) => anim(`d${i}`)} lineStyle={anim("lines")} />
        <g data-a="" style={anim("gone")}>
          <text x={tplCx} y={TPL.y + TPL.h / 2 + 5} fontSize={14} fontWeight={700} textAnchor="middle" style={{ fill: "hsl(var(--primary-text))" }}>{s.gone}</text>
        </g>
      </svg>
    </AnimatedFigure>
  );
};

export default DissolveFigure;
